import { AfterContentInit, AfterViewInit, Attribute, ContentChild, Directive, inject, Injector, Optional } from '@angular/core';
import { FormBuilder, FormGroup } from "@angular/forms";
import { DatePipe } from "@angular/common";

import {
  StylableComponent,
  provideAsWidgetRef,
  Parent,
  WIDGET_CONSTANTS
} from "@wm/components-base";
import { FormWidgetType, isDateTimeType, isDefined, noop, runtimeEventManager, getVariableName, getDateTimeFormatForType, stringStartsWith } from '@wm/core';
import { camelCase, forEach, get, includes, isArray, isEqual, isObject, isString, join, set, startsWith } from 'lodash-es';
import { getEvaluatedData, getFormFieldClass, setPropertiesTextWidget } from "../../../formWidgetUtils";
import { FormComponent } from "../form.component";
import {LiveFilterDirective} from "../live-filter.directive";
import {getAppDefault} from "../../../input/utils/appDefaults";

let resetDataSetProps = ['datafield', 'searchkey', 'displaylabel', 'displayfield', 'displayexpression', 'orderby', 'displayimagesrc'];
let toBeRemovedAttrs = ['widgettype', 'widgetid', 'widget-id', 'candelete', 'wm-resizable', 'wm-widget-overlay', 'is-container', 'movable-in-canvas',
  'is-template-bundled', 'is-layout-bundled', 'show-match', 'show-count', 'show-collapsible', 'show-date-format', 'wm-widget-overlay-drag', 'wm-widget-drag', 'wm-droppable', 'wm-droppable-parent', 'part', 'newcolumns', 'scaffold'];

@Directive({
  selector: '[wmFormField]',
  providers: [
    provideAsWidgetRef(FormFieldDirective),
    DatePipe
  ],
  standalone: true,
  exportAs: 'wmFormField'
})
export class FormFieldDirective extends StylableComponent implements AfterContentInit, AfterViewInit {
  @ContentChild('formWidget', {static: true}) formWidget: any;
  @ContentChild('formWidgetMax') formWidgetMax: any;
  private typeUtilsService: any;

  displayname: string | null;
  matchmode?: string;
  fieldDefConfig: any = {};
  liveWidgetSettingsService: any;
  liveWidgetMarkupManager: any;
  pcDisplay: any;
  mobileDisplay: any;
  tabletDisplay: any;
  inputtype?: string;
  trailingzero: any;
  step: any;
  updateon: any;
  match: any;
  displaywidget: string; // this contains widget type of formfield like radioset, text, select
  widgetname: string // this refers the custom widget used
  ngclass: string;
  liveWidgetUtilsService: any;
  private ngform: FormGroup;
  private _fieldName: string;
  private fb: FormBuilder;
  private _validators: any;
  private type: string;
  private isRelated: boolean;
  private binddataset: string;
  private variableService: any;
  private resetProps: {[key: string]: any} = {}
  private filterexpressions: string;
  private filterOn: string | undefined;
  private show: boolean = false;
  private captionposition: any;
  private bindrequired: string;
  private _initPropsRes;
  private viewmodewidget: string;
  private bindshow: string;
  private displayexpression: string;
  private outputformat: string;
  private datepattern: string;
  private timepattern: string;
  private currency: string;
  private datavalue: any;

  constructor(inj: Injector, @Optional() viewParent: Parent,
              @Optional() public form: FormComponent,
              @Optional() public liveFilter: LiveFilterDirective,
              fb: FormBuilder,
              @Attribute('displaywidget') private _widgetType: string,
              @Attribute('widget') private _widget: string,
              @Attribute('widgetname') private _widgetname: string,
              @Attribute('displayname') _displayname: string,
              @Attribute('display-name') _displayName: string,
              @Attribute('chipclass.bind') bindChipclass: string,
              @Attribute('dataset.bind') binddataset: string,
              @Attribute('displayexpression.bind') binddisplayexpression: string,
              @Attribute('displaylabel.bind') binddisplaylabel: string,
              @Attribute('name') private name: string,
              @Attribute('displayimagesrc.bind') binddisplayimagesrc: string,
              @Attribute('key') private key: any,
              @Attribute('field') private field: string,
              @Attribute('is-range') private isRange: boolean) {
    const WIDGET_CONFIG = {
      widgetType: 'wm-form-field',
      hostClass: '',
      widgetSubType: 'wm-form-field-' + (_widgetType || _widget || FormWidgetType.TEXT).trim()
    };
    let resolveFn: Function = noop;
    super(inj, WIDGET_CONFIG, viewParent, new Promise(res => resolveFn = res));
    this.key = this.key
    this.field = this.field || this.key;
    this._initPropsRes = resolveFn;
    this.displayname = _displayname || _displayName;
    this._fieldName = key || name;
    this.liveWidgetSettingsService = runtimeEventManager.getService('LiveWidgetSettingsService');
    this.liveWidgetMarkupManager = runtimeEventManager.getService('LiveWidgetMarkupManagerService');
    this.liveWidgetUtilsService = runtimeEventManager.getService('LiveWidgetUtilsService');
    this.fb = fb;
    this.displaywidget = _widgetType || _widget || 'text';
    this.widgetname = _widgetname || 'text';
    this.registerFormField();
    this.variableService = runtimeEventManager.getService('variableService');
  }

  override onStyleChange(key: string, nv: any, ov?: any) {
    this.setFormWidget(key, nv);
    this.setMaxFormWidget(key, nv);
    super.onStyleChange(key, nv, ov);
  }

  get _control() {
    return this.ngform && this.ngform.controls[this._fieldName];
  }

  getFormWidgetProps(customWidgetSubType ?: string) {
    this.liveWidgetUtilsService = runtimeEventManager.getService('LiveWidgetUtilsService');
    let displayWidget = customWidgetSubType ? customWidgetSubType.split("-").pop() : this.displaywidget;
    if (!displayWidget) {
      displayWidget = this.nativeElement.getAttribute('displaywidget') || this.nativeElement.getAttribute('widget') || 'text';
    }
    let _wp: any = this.liveWidgetUtilsService.getWidgetProps(displayWidget, 'wm-form-field', customWidgetSubType ? customWidgetSubType : this.widgetSubType);
    if (displayWidget === 'text') {
      this.inputtype = this.$attrs.get('inputtype') || 'text';
    } else if (displayWidget === 'number') {
      this.inputtype = 'number';
    } else if (displayWidget === 'password') {
      this.inputtype = 'password';
    }
    return _wp;
  }

  override ngOnInit() {
    super.ngOnInit();
    this.typeUtilsService = runtimeEventManager.getService('TypeFactoryService');
  }

  setFieldDefConfig() {
    if (!this.form) {
      let formEl = $(this.nativeElement).closest('[wmform][widgetid]');
      if (formEl.length) {
        // @ts-ignore
        this.form = get(formEl[0], 'widget');
      }
    }
    if (this.form) {
      this.ngform = this.form.ngform;
      this.captionposition = this.form.captionposition;
    }

    this.fieldDefConfig.displayname = this.displayname || this.form.getBindedVal('displayname', this.nativeElement);
    if (!this.displayname) {
      this.displayname = this.form.getBindedVal('displayname', this.nativeElement);
    }
    this.fieldDefConfig.matchmode = this.$attrs.get('matchmode');
    this.fieldDefConfig.match = this.$attrs.get('match');
    if (isDefined(this.fieldDefConfig.matchmode)) {
      this.matchmode = this.fieldDefConfig.matchmode;
    }
    this.fieldDefConfig.filterOn = this.filterOn = this.$attrs.get('filter-on');
    this.fieldDefConfig.show = isDefined(this.$attrs.get('show')) ? (this.$attrs.get('show') === 'false' ? false : (this.$attrs.get('show') === 'true' || this.$attrs.get('show'))) : true;
    if (isDefined(this.$attrs.get('primary-key'))) {
      this.fieldDefConfig.primaryKey = this.$attrs.get('primary-key');
      this.fieldDefConfig.show = this.$attrs.get('primary-key')?.toString() !== 'true';
    }
    this.fieldDefConfig.isRelated = this.$attrs.get('is-related')  === 'false' ? false : (this.$attrs.get('is-related') === 'true' || this.$attrs.get('is-related'));
    this.fieldDefConfig.widgettype = this.$attrs.get('widgettype') || 'text';
    this.fieldDefConfig.generator = this.$attrs.get('generator');
    this.fieldDefConfig.placeholder = this.$attrs.get('placeholder');
    this.fieldDefConfig.maxplaceholder = this.$attrs.get('maxplaceholder');
    this.fieldDefConfig.required = this.bindrequired || includes(['checkboxset', 'chips', 'rating', 'silder', 'richtext'], this.widgetType) ? false : this.$attrs.get('required');
    this.fieldDefConfig._readonly = this.$attrs.get('readonly');
    this.fieldDefConfig.regexp = this.$attrs.get('regexp');
    this.fieldDefConfig.type = this.$attrs.get('type') || 'text';
    this.key = this.$attrs.get('key') || this.$attrs.get('name') ||this.$attrs.get('field');
    this.fieldDefConfig.key = this.key;
    this.fieldDefConfig.name = this.$attrs.get('name') || this.$attrs.get('key') || this.$attrs.get('field');
    this.fieldDefConfig.mobileDisplay = this.fieldDefConfig.tabletDisplay = this.fieldDefConfig.pcDisplay = undefined;
    this.fieldDefConfig.isRange = this.$attrs.get('is-range') === 'false' ? undefined : (this.$attrs.get('is-range') === 'true' || this.$attrs.get('is-range'));
    if (isDefined(this.$attrs.get('mobile-display'))) {
      this.fieldDefConfig.mobileDisplay = this.$attrs.get('mobile-display') === 'false' ? false : (this.$attrs.get('mobile-display') === 'true' || this.$attrs.get('mobile-display'));
      this.mobileDisplay = this.fieldDefConfig.mobileDisplay;
    } else {
      this.fieldDefConfig.mobileDisplay = true;
      this.mobileDisplay = true;
    }
    if (isDefined(this.$attrs.get('pc-display'))) {
      this.fieldDefConfig.pcDisplay = this.$attrs.get('pc-display') === 'false' ? false : (this.$attrs.get('pc-display') === 'true' || this.$attrs.get('pc-display'));
      this.pcDisplay = this.fieldDefConfig.pcDisplay;
    } else {
      this.fieldDefConfig.pcDisplay = true;
      this.pcDisplay = true;
    }
    if (isDefined(this.$attrs.get('tablet-display'))) {
      this.fieldDefConfig.tabletDisplay = this.$attrs.get('tablet-display') === 'false' ? false : (this.$attrs.get('tablet-display') === 'true' || this.$attrs.get('tablet-display'));
      this.tabletDisplay = this.fieldDefConfig.tabletDisplay;
    } else {
      this.fieldDefConfig.tabletDisplay = true;
      this.tabletDisplay = true;
    }
    this.fieldDefConfig.validationmessage = this.$attrs.get('validationmessage');
    this.viewmodewidget = this.$attrs.get('viewmodewidget') || this.liveWidgetUtilsService.getDefaultViewModeWidget(this.displaywidget);
    this.fieldDefConfig.viewmodewidget = this.viewmodewidget;
    this.fieldDefConfig.displaywidget = this.displaywidget;
    this.fieldDefConfig.ngclass = this.$attrs.get('ngclass');
    this.type = this.fieldDefConfig.type;
    this.isRelated = this.fieldDefConfig.isRelated;
    this.ngclass = this.fieldDefConfig.ngclass;
    if (this.isRelated) {
      this.fieldDefConfig.orderby = this.$attrs.get('orderby');
      this.fieldDefConfig.filterexpressions = this.$attrs.get('filterexpressions');
      this.fieldDefConfig.groupby = this.$attrs.get('groupby');
      this.fieldDefConfig.displayexpression = this.$attrs.get('displayexpression');
      this.fieldDefConfig.match = this.$attrs.get('match');
      this.fieldDefConfig.lookupField =this.$attrs.get('lookup-field');
      this.fieldDefConfig.lookupType = this.$attrs.get('lookupType');
      this.fieldDefConfig.relatedEntityName = this.$attrs.get('relatedEntityName');
    }
    if (isDefined(this.fieldDefConfig.displayexpression)) {
      this.displayexpression = this.fieldDefConfig.displayexpression;
    }
    if (this.displaywidget === 'autocomplete' || this.displaywidget === 'chips') {
      this.fieldDefConfig.searchkey = this.$attrs.get('searchkey');
      this.fieldDefConfig.matchmode = this.$attrs.get('matchmode');
    }
    this.filterexpressions = this.fieldDefConfig.filterexpressions;
    if (!this.bindshow && this.fieldDefConfig.show.toString() === 'false' ) {
      this.nativeElement.setAttribute('hidden', '');
    }
    this.fieldDefConfig.widgetid = this.widgetId;
    this.fieldDefConfig.maxchars = this.$attrs.get('maxchars') || undefined;

    if (this.displaywidget === 'text') {
      this.inputtype = this.$attrs.get('inputtype') || 'text';
    } else if (this.displaywidget === 'number') {
      this.inputtype = 'number';
    } else if (this.displaywidget === 'password') {
      this.inputtype = 'password';
    }
    if (isDateTimeType(this.type) || this.displaywidget === 'date' || this.displaywidget === 'datetime') {
      this.fieldDefConfig.datepattern = this.formWidget.datepattern;
      this.fieldDefConfig.timepattern = this.formWidget.timepattern;
      this.fieldDefConfig.outputformat = this.formWidget.outputformat || getDateTimeFormatForType(this.type);
      this.outputformat = this.fieldDefConfig.outputformat;
    }

    //If no pattern is provideded then set app default format
    if (['date', 'datetime', 'time', 'currency'].includes(this.displaywidget)) {
      const attrFormatMap: {
        date : {attr: 'datepattern', format: string},
        datetime : {attr: 'datepattern', format: string},
        time : {attr: 'timepattern', format: string},
        currency: {attr: 'currency', format: string}
      } = {
        date : {attr: 'datepattern', format: 'dateFormat'},
        datetime : {attr: 'datepattern', format: 'dateTimeFormat'},
        time : {attr: 'timepattern', format: 'timeFormat'},
        currency: {attr: 'currency', format: 'currencyCode'}
      };
      const displaywidget = this.displaywidget as 'date' | 'datetime' |'time' |'currency';
      const _pattern = this.$attrs.get(attrFormatMap[displaywidget].attr);
      if (!_pattern) {
        let _pattern = getAppDefault(attrFormatMap[displaywidget].format);
        if (!_pattern) {
          runtimeEventManager.on('wms:app-defaults-ready').subscribe(() => {
            this[attrFormatMap[displaywidget].attr] = getAppDefault(attrFormatMap[displaywidget].format);
          });
        } else {
          this[attrFormatMap[displaywidget].attr] = _pattern;
        }
      }
    }

    this.fieldDefConfig.inputtype = this.inputtype;

    let attrs = this.liveWidgetMarkupManager?.markupManagerService?.markup?.find('[widgetid="' + this.widgetId + '"]')[0]?.attributes;
    forEach(attrs, (attr: Attr) => {
      if(attr.name.startsWith('on-')) {
        let name = camelCase(attr.name);
        this.fieldDefConfig[name] = attr.value;
      } else if (!this.fieldDefConfig[attr.name] && !toBeRemovedAttrs.includes(attr.name)) {
        this.fieldDefConfig[attr.name] = attr.value;
      }
    });
  }

  override ngAfterViewInit() {
    super.ngAfterViewInit();
  }

  createControl() {
    return this.fb.control(undefined, {
      validators: this._validators
    });
  }

  private registerFormField() {
    if (this.ngform) {
      const fieldName = this._fieldName;
      this.ngform.addControl(fieldName, this.createControl());
    }
  }

  override onPropertyChange(key: string, newVal: any, oldVal?: any) {
    if (!isEqual(newVal, oldVal)) {
      this.setFormWidget(key, newVal);
    }
    switch (key) {
      case 'dataset':
        /*if studio-mode, then update the displayField & dataField in property panel for dataset widgets*/
        if (this.liveWidgetUtilsService.isDataSetWidgets[this.displaywidget]) {
          runtimeEventManager.emit('updatePropPanelOptions', this);
          // updatePropertyPanelOptions(scope);

          this.updateLimitProperty(newVal);
          if (this.displaywidget === 'autocomplete' || this.displaywidget === 'chips') {
            this.widgetProps['matchmode'].show = false;
            let isBoundToVariable = stringStartsWith(this.binddataset, 'bind:Variables.');
            if (isBoundToVariable) {
              let matchModeAttr = this.$element.attr('matchmode');
              let variableName = getVariableName(this);
              let variable = this.variableService.getVariableByName(variableName);
              if (variable && variable.category === 'wm.LiveVariable') {
                this.widgetProps['matchmode'].show = true;
                // set default matchmode when no value is set.
                if (!matchModeAttr) {
                  runtimeEventManager.emit('set-markup-attr', {widgetId: this.widgetId, matchmode: 'startignorecase'});
                  // $rs.$emit('set-markup-attr', scope.widgetid, {'matchmode': 'startignorecase'});
                }
              } else if (matchModeAttr) { // empty matchmode when dataset is set other than liveVariable
                runtimeEventManager.emit('set-markup-attr', {widgetId: this.widgetId, matchmode: ''});
                // $rs.$emit('set-markup-attr', scope.widgetid, {'matchmode': ''});
              }
            }
          }

          //Refresh the properties panel sub group show/false as limit property is updated
          runtimeEventManager.emit('wms:refresh-properties-panel');
          //For checkboxset and radioset, compile the field again to reflect the change in studio mode
          // if (scope.widget === 'checkboxset' || scope.widget === 'radioset') {
          //   compileField();
          // }
          this.updateGroupBy();
        }
        break;
      case 'inputtype':
        setPropertiesTextWidget(this.widgetProps, newVal);
        break;
      case 'inputmode':
        this.checkForInputMode(oldVal, newVal);
        break;
      case 'showcustompicker':
        this.widgetProps['dataentrymode'].show = newVal;
        this.widgetProps['showdropdownon'].show = newVal;
        break;
      case 'displayformat':
        this.widgetProps['showdisplayformaton'].show = newVal ? true : false;
        break;
      case 'show':
        if (newVal) {
          //TODO: Added null check to fix console error, check why markupManager is not loaded
          if (this.form)
          this.liveWidgetMarkupManager?.updateFieldMarkup({'formName': this.form['name'], 'fieldName': this.name});
          this.$element.parents('[widgettype="wm-gridcolumn"]').removeClass('hide');
        }
        break;
      case 'displayname':
        this.displayname = newVal;
        break;
      case 'placeholder':
        this.widgetProps['maxplaceholder'].show=this.$attrs.get("is-range")==="true";
        break;
      case 'maxplaceholder':
        //In case of range, add the placeholder to the second widget
          if( this.$element.find('.form-control').length>1) {
              this.$element.find('.form-control').last().attr('placeholder', newVal);
          }
        break;
      case 'groupby':
          this.updateGroupBy();
          if (newVal && newVal !== '') {
            if (newVal === WIDGET_CONSTANTS.EVENTS.JAVASCRIPT) {
              this.widgetProps['groupby'].isGroupBy = true;
            } else {
              this.widgetProps['groupby'].isGroupBy = false;
            }
          }

        break;
      case 'match':
        this.widgetProps['dateformat'].show = includes(['day', 'hour', 'month', 'week'], this.match);
        break;
      case 'disabled':
      case 'readonly':
      case 'validationmessage':
      case 'hint':
        break;
      case 'required':
        if (newVal) {
          this.$element.find('.formfield-label').addClass('required');
        } else {
          this.$element.find('.formfield-label').removeClass('required');
        }
        break;
      case 'active':
        if (newVal) {
          let _widget = this.$attrs.get('displaywidget') || this.$attrs.get('widget');
          if (_widget === 'password' || _widget === 'text') {
            setPropertiesTextWidget(this.widgetProps, this.inputtype, this);
          } else if (_widget && this.liveWidgetUtilsService.isDataSetWidgets[_widget]) {
            runtimeEventManager.emit('updatePropPanelOptions', this);
            // updatePropertyPanelOptions(scope);
            this.updateGroupBy();
            this.updateLimitProperty(this.nativeElement.getAttribute('dataset.bind'));
          }
        }
        break;
      case 'maxchars':
        if (newVal > 0 && this.widgetProps['maxchars'] && this.displaywidget === 'textarea') {
          this.widgetProps['limitdisplaytext'].show = true;
        }
        break;
      case 'datavalue':
        if (isString(newVal) && newVal.startsWith('bind:')) {
          return;
        }
        if (this._control && this.widgetType !== FormWidgetType.UPLOAD) {
          this._control.setValue(newVal);
          setTimeout(() => {
            this.formWidget.datavalue = newVal;
          })
        }
        break;
      case 'defaultvalue': 
        if (isString(newVal) && newVal.startsWith('bind:')) {
          return;
        }
        this.formWidget.nativeElement.value = newVal;
        break;
    }
    super.onPropertyChange(key, newVal, oldVal);
  }

  checkForInputMode(oldVal: any, newVal: any) {
    if (!oldVal) {
      return;
    }
    let step, trailingzero, updateon;
    if (newVal === 'financial') {
      trailingzero = true;
      step = 0.01;
      updateon = 'default';

    } else if (newVal === 'natural') {
      trailingzero = false;
      step = '';
      updateon = 'blur';
    }
    this.step = step;
    this.trailingzero = trailingzero;
    this.updateon = updateon;
    runtimeEventManager.emit('set-markup-attr', {widgetId: this.widgetId, 'step': step, 'trailingzero': trailingzero, 'updateon': updateon})
  }

  updateGroupBy() {
    if (this.widgetProps['groupby'] && this.liveWidgetSettingsService) {
      this.liveWidgetUtilsService.showOrHideMatchProperty(this, this.widgetProps);
      if (this.widgetProps['dateformat'])
      this.widgetProps['dateformat'].show = includes(['day', 'hour', 'month', 'week'], this.match);
    }
  }

  prepareFormWidget() {
    this._initPropsRes();
    this.registerReadyStateListener(() => {
      this.setFieldDefConfig();
    });

    // Register the form field with parent form
    this.form?.registerFormFields(this.widget);
  }

  ngAfterContentInit() {
    if(this.widgetSubType === "wm-form-field-custom-widget" && this.formWidget.nativeElement)
      this.formWidget = this.formWidget.nativeElement.widget;
    if (this.formWidget) {
      if (!this.form) {
        let formEl = $(this.nativeElement).closest('[wmform][widgetid]');
        if (formEl.length) {
          // @ts-ignore
          this.form = get(formEl[0], 'widget');
        }
      }
      if(this.widgetSubType === "wm-form-field-custom-widget") {
        this.formWidget.configSubject.asObservable().subscribe(() => {
          this.prepareFormWidget();
        });
      } else {
        this.prepareFormWidget();
      }
    }
    const parentElementClass = this.$element.parents('[widgettype="wm-form"], [widgettype="wm-liveform"], [widgettype="wm-livefilter"]').attr('itemsperrow');
    if (parentElementClass) {
      const formFieldClass = getFormFieldClass(parentElementClass)
      setTimeout(() => {
        this.$element.addClass(formFieldClass)
      });
    }
  }

  // Expression to be evaluated in view mode of form field
  getDisplayExpr() {
    const caption = [];
    const value = this.datavalue;
    const displayField = this.$attrs.get('displayfield') || this.$attrs.get('displaylabel') || '';
    const displayExpr = this.$attrs.get('displayexpression') || '';
    if (isObject(value)) {
      if (isArray(value)) {
        forEach(value, obj => {
          if (isObject(obj)) {
            caption.push(this.evaluateExpr(obj, displayField, displayExpr));
          }
        });
      } else {
        caption.push(this.evaluateExpr(value, displayField, displayExpr));
      }
      return join(caption, ',');
    }
    return (value === undefined || value === null) ? '' : this.datavalue;
  }

  // Expression to be evaluated in view mode of form field
  evaluateExpr(object: any, displayField: string, displayExpr: string) {
    if (!displayExpr || !displayField) {
      displayField = Object.keys(object)[0];
      // If dataset is not ready, display expression will not be defined
      if (!displayField) {
        return;
      }
    }
    return getEvaluatedData(object, {
      field: displayField,
      expression: displayExpr
    }, this.viewParent);
  }

  // Method to set the properties on inner form widget
  setFormWidget(key: string, val: any) {
    if (this.formWidget && this.formWidget?.nativeElement?.widget) {
      if (this.formWidget.nativeElement.widget?.widgetType === 'wm-text' && key === 'class') {
        this.formWidget.nativeElement.widget[key] = 'form-control ' + val;
      } else {
      this.formWidget.nativeElement.widget[key] = val;
      }
      if (key ==='inputtype'){
        this.formWidget.nativeElement.widget['type'] = val;
      }
    }
  }

  // Method to set the properties on inner max form widget (when range is selected)
  setMaxFormWidget(key: string, val: any) {
    if (this.formWidgetMax) {
      this.formWidgetMax.widget[key] = val;
    }
  }

  getCustomizedOptions(name: any, fields: any){
    return this.liveWidgetUtilsService.getCustomizedOptions(this, name, fields);
  }

  private updateLimitProperty(newVal: any) {
    if (isDefined(newVal) && newVal !== null) {
      if (startsWith(this.binddataset, 'bind:') ||(this.getAttr('dataset.bind') && newVal!="")) {
        if (this.widgetProps['limit'])
          this.widgetProps['limit'].show = this.displaywidget === 'autocomplete';
      } else {
          if(this.displaywidget==='checkboxset' && this.getAttr('dataset.bind')){
              return;
          }
        forEach(resetDataSetProps, (prop) => {
          this.resetProps[prop] = '';
          this.fieldDefConfig[prop] = undefined;
          set(this, prop, undefined);
        });
        runtimeEventManager.emit('set-markup-attr', {widgetId: this.widgetId, ...this.resetProps});
        // $rs.$emit('set-markup-attr', scope.widgetid, this.resetProps);
        if (this.widgetProps['limit'])
          this.widgetProps['limit'].show = true;
      }
      this.nativeElement.removeAttribute('data-evaluated-dataset');
    } else {
      //Show limit if dataset is not bound
      if (this.widgetProps['limit'])
        this.widgetProps['limit'].show = true;
    }
  }

  getDataValueType() {
    if (!this.typeUtilsService) {
      this.typeUtilsService = runtimeEventManager.getService('TypeFactoryService');
    }
    let propertyType,
      bindDataSet = this.binddataset;
    //If dataset is not bound, get it from liveform dataset
    if (!bindDataSet && get(this.form, 'binddataset')) {
      bindDataSet = get(this.form, 'binddataset') + '.' + this.key;
    }
    propertyType = this.typeUtilsService.getTypeForExpression(bindDataSet);
    return propertyType;
  }
}

